import { createClient } from "@supabase/supabase-js";
import bcrypt from "bcryptjs";
import { buildCheckoutPayload } from "../utils/cashierStock";
import { calculateTransactionProfit } from "../utils/dashboardProfit";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

function apiError(message, details) {
  const err = new Error(message);
  err.response = { data: { error: message, details } };
  return err;
}

// Auth
export async function login(username, password) {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw apiError(
      "Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables.",
    );
  }

  const email = `${username.trim()}@store.local`;
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw apiError("Invalid username or password");

  // Get role from our users table
  const { data: userData, error: userError } = await supabase
    .from("users")
    .select("id, username, role, full_name")
    .eq("username", username.trim())
    .maybeSingle();

  if (userError || !userData) throw apiError("User account not found");

  return {
    token: data.session.access_token,
    user: {
      id: userData.id,
      username: userData.username,
      role: userData.role,
      fullName: userData.full_name,
    },
  };
}

export async function logout() {
  await supabase.auth.signOut();
}

const normalizeBarcode = (barcode) =>
  String(barcode ?? "").trim().toLowerCase();

export function attachSharedBarcodeCounts(units) {
  const activeCounts = new Map();

  for (const unit of units || []) {
    if (unit.is_active === false) continue;
    const barcode = normalizeBarcode(unit.barcode);
    if (!barcode) continue;
    activeCounts.set(barcode, (activeCounts.get(barcode) || 0) + 1);
  }

  return (units || []).map((unit) => {
    const barcode = normalizeBarcode(unit.barcode);
    return {
      ...unit,
      shared_barcode_count:
        unit.is_active === false || !barcode ? 0 : activeCounts.get(barcode) || 0,
    };
  });
}

export function normalizeResolvedBarcodeOption(resolved) {
  return {
    id: resolved.itemId,
    itemId: resolved.itemId,
    unitId: resolved.unitId,
    variantId: resolved.variantId,
    barcode: resolved.barcode,
    name: resolved.productName,
    unitName: resolved.unitName,
    price: resolved.price,
    stock: resolved.stock,
    conversionFactor: resolved.conversionFactor,
    availableUnits: resolved.availableUnits,
    isVariant: resolved.kind === "legacy_variant",
    isProductUnit: resolved.kind === "packaging_unit",
  };
}

export function normalizeBarcodeLookupResult(resolved) {
  if (resolved?.kind === "shared_packaging_units") {
    return {
      requiresChoice: true,
      options: (resolved.options || []).map(normalizeResolvedBarcodeOption),
    };
  }
  return normalizeResolvedBarcodeOption(resolved);
}

// Items
export async function getItems() {
  const { data, error } = await supabase
    .from("items")
    .select("*")
    .order("name")
    .limit(5000);
  if (error) throw apiError(error.message);
  return data;
}

export async function getInventoryItems() {
  const [itemsResult, unitsResult] = await Promise.all([
    supabase.from("items").select("*").order("name").limit(5000),
    supabase
      .from("product_units")
      .select("*")
      .order("display_order")
      .order("id")
      .limit(10000),
  ]);

  if (itemsResult.error) throw apiError(itemsResult.error.message);
  if (unitsResult.error) throw apiError(unitsResult.error.message);

  const unitsByItem = new Map();
  for (const unit of attachSharedBarcodeCounts(unitsResult.data || [])) {
    const key = String(unit.item_id);
    const itemUnits = unitsByItem.get(key) || [];
    itemUnits.push(unit);
    unitsByItem.set(key, itemUnits);
  }

  return (itemsResult.data || []).map((item) => ({
    ...item,
    product_units: unitsByItem.get(String(item.id)) || [],
  }));
}

export async function getItemsPaginated({
  page = 1,
  pageSize = 24,
  search = "",
  includeCount = true,
} = {}) {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const normalizedSearch = search.trim();
  let matchingUnitItemIds = [];

  if (normalizedSearch) {
    const { data: matchingUnits, error: unitSearchError } = await supabase
      .from("product_units")
      .select("item_id")
      .eq("is_active", true)
      .or(
        `unit_name.ilike.%${normalizedSearch}%,barcode.ilike.%${normalizedSearch}%`,
      );
    if (unitSearchError) throw apiError(unitSearchError.message);
    matchingUnitItemIds = [
      ...new Set((matchingUnits || []).map((unit) => Number(unit.item_id))),
    ];
  }

  let query = supabase
    .from("items")
    .select(
      "id, barcode, name, price, base_price, category, stock, unit_inventory_enabled",
      includeCount ? { count: "exact" } : {},
    )
    .order("name");
  if (normalizedSearch) {
    const unitIdFilter = matchingUnitItemIds.length
      ? `,id.in.(${matchingUnitItemIds.join(",")})`
      : "";
    query = query.or(
      `name.ilike.%${normalizedSearch}%,barcode.ilike.%${normalizedSearch}%${unitIdFilter}`,
    );
  }
  const { data, error, count } = await query.range(from, to);
  if (error) throw apiError(error.message);

  const pageItems = data || [];
  const pageItemIds = pageItems.map((item) => item.id);
  let units = [];
  if (pageItemIds.length) {
    const { data: pageUnits, error: unitsError } = await supabase
      .from("product_units")
      .select("*")
      .in("item_id", pageItemIds)
      .order("display_order")
      .order("id");
    if (unitsError) throw apiError(unitsError.message);
    units = attachSharedBarcodeCounts(pageUnits || []);
  }

  const unitsByItem = new Map();
  for (const unit of units) {
    const key = String(unit.item_id);
    const itemUnits = unitsByItem.get(key) || [];
    itemUnits.push(unit);
    unitsByItem.set(key, itemUnits);
  }

  return {
    items: pageItems.map((item) => ({
      ...item,
      product_units: unitsByItem.get(String(item.id)) || [],
    })),
    total: count,
  };
}

export async function getItemById(id) {
  const { data, error } = await supabase
    .from("items")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw apiError(error.message);
  if (!data) throw apiError("Item not found");
  return data;
}

export async function getItemByBarcode(barcode) {
  const trimmedBarcode = String(barcode).trim();
  const { data: resolved, error: lookupError } = await supabase.rpc(
    "lookup_inventory_barcode",
    { p_barcode: trimmedBarcode },
  );
  if (!lookupError && resolved) {
    return normalizeBarcodeLookupResult(resolved);
  }
  // Application rollback compatibility: use the legacy lookup while the
  // additive RPC has not yet been deployed, but surface genuine RPC failures.
  if (lookupError && lookupError.code !== "42883") {
    throw apiError(lookupError.message || "Barcode lookup failed");
  }
  const { data, error } = await supabase
    .from("items")
    .select("*")
    .eq("barcode", trimmedBarcode)
    .eq("unit_inventory_enabled", false)
    .maybeSingle();
  if (error) throw apiError(error.message);
  if (data) return data;

  throw apiError("Item not found. Please add it in Admin section first.");
}

export async function getCashierItems() {
  const [items, unitsResult] = await Promise.all([
    getItems(),
    supabase
      .from("product_units")
      .select("*")
      .eq("is_active", true)
      .eq("can_sell", true)
      .not("barcode", "is", null)
      .limit(5000),
  ]);
  const unitTableMissing = ["42P01", "PGRST205"].includes(unitsResult.error?.code);
  const units = unitTableMissing
    ? []
    : attachSharedBarcodeCounts(unitsResult.data || []);
  if (unitsResult.error && !unitTableMissing) {
    throw apiError(unitsResult.error.message);
  }

  const itemsById = new Map(items.map((item) => [String(item.id), item]));
  const unitItems = units.flatMap((unit) => {
    const parentItem = itemsById.get(String(unit.item_id));
    if (!parentItem) return [];
    return [{
      ...parentItem,
      browserKey: `unit-${unit.id}`,
      itemId: parentItem.id,
      unitId: unit.id,
      barcode: unit.barcode,
      name: `${parentItem.name} — ${unit.unit_name}`,
      unitName: unit.unit_name,
      price: unit.selling_price,
      stock: parentItem.stock,
      conversionFactor: unit.conversion_factor,
      availableUnits: Math.floor(
        Math.max(0, parentItem.stock || 0) / unit.conversion_factor,
      ),
      isVariant: false,
      isProductUnit: true,
    }];
  });

  return [
    ...items
      .filter((item) => !item.unit_inventory_enabled)
      .map((item) => ({
        ...item,
        browserKey: `item-${item.id}`,
        itemId: item.id,
        isVariant: false,
      })),
    ...unitItems,
  ];
}

export async function getProductUnits(itemId) {
  const { data, error } = await supabase
    .from("product_units")
    .select("*")
    .eq("item_id", itemId)
    .order("display_order")
    .order("id");
  if (error) throw apiError(error.message);
  return data || [];
}

export async function updateProductUnits(payload) {
  const { data, error } = await supabase.rpc("update_product_units", {
    p_item_id: payload.itemId,
    p_units: payload.units,
    p_reason: payload.reason,
  });
  if (error) throw apiError(error.message, error.details);
  return data;
}

export async function generateProductUnitBarcode(itemId, unitCode) {
  const { data, error } = await supabase.rpc("generate_product_unit_barcode", {
    p_item_id: itemId,
    p_unit_code: unitCode || "UNIT",
  });
  if (error) throw apiError(error.message, error.details);
  return data;
}

export async function createItem(itemData) {
  if (Array.isArray(itemData.units) && itemData.units.length > 0) {
    const trimmedBarcode = String(itemData.barcode || "").trim();
    const { data, error } = await supabase.rpc("create_item_with_units", {
      p_barcode: trimmedBarcode,
      p_name: itemData.name,
      p_price: parseFloat(itemData.price),
      p_base_price: itemData.base_price ? parseFloat(itemData.base_price) : null,
      p_category: itemData.category || null,
      p_opening_base_stock: parseInt(itemData.stock, 10) || 0,
      p_units: itemData.units,
    });
    if (error) throw apiError(error.message, error.details);
    return data;
  }

  const trimmedBarcode = String(itemData.barcode).trim();
  const { data: existingItem } = await supabase
    .from("items")
    .select("id")
    .ilike("barcode", trimmedBarcode)
    .maybeSingle();
  if (existingItem) throw apiError("An item with this barcode already exists");

  const { data, error } = await supabase.rpc("create_inventory_item", {
    p_barcode: trimmedBarcode,
    p_name: itemData.name,
    p_price: parseFloat(itemData.price),
    p_base_price: itemData.base_price ? parseFloat(itemData.base_price) : null,
    p_category: itemData.category || null,
    p_stock: parseInt(itemData.stock, 10) || 0,
  });
  if (error) throw apiError(error.message);
  return data;
}

export async function updateItem(id, itemData) {
  if (Array.isArray(itemData.units) && itemData.units.length > 0) {
    const trimmedBarcode = String(itemData.barcode || "").trim();
    const { data, error } = await supabase.rpc("update_item_with_units", {
      p_item_id: Number(id),
      p_barcode: trimmedBarcode,
      p_name: itemData.name,
      p_price: parseFloat(itemData.price),
      p_base_price: itemData.base_price ? parseFloat(itemData.base_price) : null,
      p_category: itemData.category || null,
      p_base_stock: parseInt(itemData.stock, 10) || 0,
      p_units: itemData.units,
    });
    if (error) throw apiError(error.message, error.details);
    return data;
  }

  const trimmedBarcode = String(itemData.barcode).trim();
  const { data: existingItem } = await supabase
    .from("items")
    .select("id")
    .ilike("barcode", trimmedBarcode)
    .neq("id", id)
    .maybeSingle();
  if (existingItem) throw apiError("An item with this barcode already exists");

  const { error } = await supabase.rpc("update_item_with_stock", {
    p_item_id: Number(id),
    p_barcode: itemData.barcode,
    p_name: itemData.name,
    p_price: parseFloat(itemData.price),
    p_base_price: itemData.base_price ? parseFloat(itemData.base_price) : null,
    p_category: itemData.category || null,
    p_stock: parseInt(itemData.stock, 10) || 0,
  });
  if (error) throw apiError(error.message);
  return { message: "Item updated", changes: 1 };
}

export async function deleteItem(id) {
  const { error } = await supabase.rpc("delete_unsold_item", {
    p_item_id: Number(id),
  });
  if (error) {
    const safeMessage = error.message?.includes("not found")
      ? "Product was not found"
      : "Product could not be deleted";
    throw apiError(safeMessage);
  }
  return { message: "Item deleted" };
}

// Categories
export async function getCategories() {
  const { data, error } = await supabase
    .from("categories")
    .select("*")
    .order("name");
  if (error) throw apiError(error.message);
  return data;
}

export async function createCategory(categoryData) {
  const { data: existing } = await supabase
    .from("categories")
    .select("id")
    .ilike("name", categoryData.name.trim())
    .maybeSingle();
  if (existing) throw apiError("Category already exists");

  const { data, error } = await supabase
    .from("categories")
    .insert({
      name: categoryData.name.trim(),
      description: categoryData.description || null,
    })
    .select()
    .single();
  if (error) throw apiError(error.message);
  return { ...data, message: "Category created" };
}

export async function updateCategory(id, categoryData) {
  const { error } = await supabase
    .from("categories")
    .update({
      name: categoryData.name.trim(),
      description: categoryData.description || null,
    })
    .eq("id", id);
  if (error) throw apiError(error.message);
  return { message: "Category updated" };
}

export async function deleteCategory(id) {
  const { data: cat, error: catErr } = await supabase
    .from("categories")
    .select("name")
    .eq("id", id)
    .maybeSingle();
  if (catErr) throw apiError(catErr.message);
  if (!cat) throw apiError("Category not found");

  const { data: items } = await supabase
    .from("items")
    .select("id")
    .eq("category", cat.name)
    .limit(1);
  if (items && items.length > 0)
    throw apiError("Cannot delete category that has items assigned to it");

  const { error } = await supabase.from("categories").delete().eq("id", id);
  if (error) throw apiError(error.message);
  return { message: "Category deleted" };
}

// Users
export async function getUsers() {
  const { data, error } = await supabase
    .from("users")
    .select("id, username, role, full_name, created_at")
    .order("id");
  if (error) throw apiError(error.message);
  return data;
}

export async function createUser(userData) {
  const { data: existing } = await supabase
    .from("users")
    .select("id")
    .eq("username", userData.username)
    .maybeSingle();
  if (existing) throw apiError("Username already exists");

  const hashedPassword = await bcrypt.hash(userData.password, 10);

  const { data, error } = await supabase
    .from("users")
    .insert({
      username: userData.username,
      password: hashedPassword,
      role: userData.role || "cashier",
      full_name: userData.fullName || null,
    })
    .select()
    .single();
  if (error) throw apiError(error.message);
  return {
    id: data.id,
    username: data.username,
    role: data.role,
    fullName: data.full_name,
    message: "User created",
  };
}

export async function deleteUser(id) {
  const { error } = await supabase.from("users").delete().eq("id", id);
  if (error) throw apiError(error.message);
  return { message: "User deleted" };
}

// Transactions
export async function getTransactions() {
  const { data: transactions, error } = await supabase
    .from("transactions")
    .select("*, transaction_items(count)")
    .order("created_at", { ascending: false });
  if (error) throw apiError(error.message);
  return transactions.map((t) => ({
    ...t,
    item_count: parseInt(t.transaction_items?.[0]?.count ?? 0),
  }));
}

export async function getTransactionById(transactionId) {
  const { data: tx, error } = await supabase
    .from("transactions")
    .select("*")
    .eq("transaction_id", transactionId)
    .maybeSingle();
  if (error) throw apiError(error.message);
  if (!tx) throw apiError("Transaction not found");

  const { data: items } = await supabase
    .from("transaction_items")
    .select("*")
    .eq("transaction_id", transactionId);

  return { ...tx, items: items || [] };
}

const parseReceiptRpcData = (data) => {
  if (typeof data !== "string") return data || {};
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const normalizeReceiptItem = (item = {}) => ({
  id: item.id,
  itemId: item.itemId ?? item.item_id,
  unitId: item.unitId ?? item.product_unit_id ?? null,
  barcode: item.barcode || "",
  name: item.name ?? item.itemName ?? item.item_name ?? "",
  unitName: item.unitName ?? item.unit_name ?? "Unit",
  quantity: Number(item.quantity ?? item.unit_quantity ?? 0),
  conversionFactor: Number(item.conversionFactor ?? item.conversion_factor ?? 1),
  baseQuantity: Number(item.baseQuantity ?? item.base_quantity ?? item.quantity ?? 0),
  unitPrice: Number(item.unitPrice ?? item.unit_price ?? 0),
  totalPrice: Number(item.totalPrice ?? item.total_price ?? 0),
});

export const normalizeReceipt = (receipt = {}) => ({
  id: receipt.id,
  transactionId: receipt.transactionId ?? receipt.transaction_id,
  createdAt: receipt.createdAt ?? receipt.created_at,
  totalAmount: Number(receipt.totalAmount ?? receipt.total_amount ?? 0),
  corrected: Boolean(receipt.corrected),
  items: (receipt.items || receipt.transaction_items || []).map(normalizeReceiptItem),
  remainingStock: receipt.remainingStock || receipt.remaining_stock || [],
  warnings: receipt.warnings || [],
});

const normalizeReceiptSummary = (receipt = {}) => ({
  id: receipt.id,
  transactionId: receipt.transactionId ?? receipt.transaction_id,
  createdAt: receipt.createdAt ?? receipt.created_at,
  totalAmount: Number(receipt.totalAmount ?? receipt.total_amount ?? 0),
  itemCount: Number(receipt.itemCount ?? receipt.item_count ?? 0),
  corrected: Boolean(receipt.corrected),
});

const mapReceiptError = (error, operation) => {
  const code = error?.code;
  if (code === "42501" && operation === "correct") {
    return apiError("Only today’s receipts can be corrected.");
  }
  if (code === "42501" && operation === "delete") {
    return apiError("Only today’s receipts can be deleted.");
  }
  if (code === "42501") {
    return apiError("Receipt access is not available for this account.");
  }
  if (
    code === "P0002" &&
    operation === "correct" &&
    /selected product is unavailable/i.test(error?.message || "")
  ) {
    return apiError("A product on this receipt is no longer available. No changes were saved.");
  }
  if (code === "P0002") {
    return apiError("Receipt could not be found.");
  }
  if (code === "22023") {
    return apiError("Review the receipt items, quantities, and prices, then try again.");
  }
  if (code === "23514") {
    return apiError("Receipt stock could not be reconciled safely. No changes were saved.");
  }
  const fallback = {
    list: "Receipts could not be loaded.",
    detail: "Receipt details could not be loaded.",
    correct: "The receipt could not be corrected. No changes were saved.",
    delete: "The receipt could not be deleted. No changes were made.",
  }[operation] || "Receipt operation failed.";
  return apiError(fallback);
};

export async function listReceipts({
  from = null,
  to = null,
  productSearch = "",
  limit = 50,
  before = null,
} = {}) {
  const { data, error } = await supabase.rpc("list_sale_receipts", {
    p_from: from,
    p_to: to,
    p_product_search: String(productSearch || "").trim() || null,
    p_limit: limit,
    p_before_created_at: before?.createdAt ?? before?.created_at ?? null,
    p_before_id: before?.id ?? null,
  });
  if (error) throw mapReceiptError(error, "list");

  const result = parseReceiptRpcData(data);
  return {
    receipts: (result.receipts || []).map(normalizeReceiptSummary),
    nextCursor: result.nextCursor || result.next_cursor || null,
  };
}

export async function getReceiptById(transactionId) {
  const { data, error } = await supabase.rpc("get_sale_receipt", {
    p_transaction_id: transactionId,
  });
  if (error) throw mapReceiptError(error, "detail");
  return normalizeReceipt(parseReceiptRpcData(data));
}

export async function correctReceipt({ transactionId, items, reason } = {}) {
  const { data, error } = await supabase.rpc("correct_sale_receipt", {
    p_transaction_id: transactionId,
    p_items: items,
    p_reason: reason || null,
  });
  if (error) throw mapReceiptError(error, "correct");
  return normalizeReceipt(parseReceiptRpcData(data));
}

export async function deleteReceipt(transactionId) {
  const { data, error } = await supabase.rpc("delete_sale_receipt", {
    p_transaction_id: transactionId,
  });
  if (error) throw mapReceiptError(error, "delete");
  return data;
}

// Sales / Checkout
export async function saveCartItemStock({ itemId, barcode, stock }) {
  const { data, error } = await supabase.rpc("set_item_stock", {
    p_item_id: itemId,
    p_barcode: String(barcode).trim(),
    p_stock: stock,
  });
  if (error) throw apiError(error.message || "Failed to update stock");
  return data;
}

export async function checkout(items) {
  const { data, error } = await supabase.rpc("checkout_sale_with_price_overrides", {
    p_items: buildCheckoutPayload(items),
  });
  if (error) {
    let details = error.details;
    try {
      details = error.details ? JSON.parse(error.details) : undefined;
    } catch {
      // Supabase may return a plain PostgreSQL detail string.
    }
    throw apiError(error.message || "Failed to process checkout", details);
  }
  return {
    transactionId: data.transactionId,
    totalAmount: data.totalAmount,
    remainingStock: data.remainingStock || [],
    warnings: data.warnings || [],
    items: data.items || [],
    message: "Checkout successful",
  };
}

// Statistics
const TRANSACTION_ITEMS_BATCH_SIZE = 100;
const PROFIT_PERIOD_MONTH_OFFSETS = {
  current_month: 0,
  last_month: -1,
  last_3_months: -2,
  last_6_months: -5,
};

const normalizeProfitPeriod = (period) =>
  Object.prototype.hasOwnProperty.call(PROFIT_PERIOD_MONTH_OFFSETS, period)
    ? period
    : "current_month";

export function getProfitPeriodRange(period = "current_month", now = new Date()) {
  const selectedPeriod = normalizeProfitPeriod(period);
  const start = new Date(
    now.getFullYear(),
    now.getMonth() + PROFIT_PERIOD_MONTH_OFFSETS[selectedPeriod],
    1,
  );
  const end = selectedPeriod === "last_month"
    ? new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, -1)
    : now;

  return { start, end };
}

export function getCalendarMonthRange(now = new Date()) {
  return getProfitPeriodRange("current_month", now);
}

export async function fetchTransactionItemsInBatches(transactionIds) {
  const items = [];
  const ids = Array.from(new Set(transactionIds || [])).filter(Boolean);

  for (let start = 0; start < ids.length; start += TRANSACTION_ITEMS_BATCH_SIZE) {
    const batch = ids.slice(start, start + TRANSACTION_ITEMS_BATCH_SIZE);
    const { data, error } = await supabase
      .from("transaction_items")
      .select("transaction_id, item_id, barcode, unit_price, quantity, base_quantity")
      .in("transaction_id", batch);
    if (error) throw apiError(error.message);
    items.push(...(data || []));
  }

  return items;
}

async function calculateProfitForTransactionIds(transactionIds) {
  if (!transactionIds.size) return 0;
  const [txItems, itemPricesRes, unitsRes] = await Promise.all([
    fetchTransactionItemsInBatches([...transactionIds]),
    supabase.from("items").select("id, barcode, base_price"),
    supabase.from("product_units").select("item_id, barcode"),
  ]);

  const queryError = itemPricesRes.error || unitsRes.error;
  if (queryError) throw apiError(queryError.message);

  return calculateTransactionProfit(
    txItems,
    itemPricesRes.data || [],
    unitsRes.data || [],
  );
}

export async function getProfitForPeriod({
  profitPeriod = "current_month",
  now = new Date(),
} = {}) {
  const selectedProfitPeriod = normalizeProfitPeriod(profitPeriod);
  const profitRange = getProfitPeriodRange(selectedProfitPeriod, now);
  const { data, error } = await supabase
    .from("transactions")
    .select("transaction_id")
    .gte("created_at", profitRange.start.toISOString())
    .lte("created_at", profitRange.end.toISOString());
  if (error) throw apiError(error.message);

  const totalProfit = await calculateProfitForTransactionIds(
    new Set((data || []).map((transaction) => transaction.transaction_id)),
  );

  return {
    period: selectedProfitPeriod,
    start: profitRange.start.toISOString(),
    end: profitRange.end.toISOString(),
    total_profit: totalProfit,
  };
}

export async function getStatistics({ profitPeriod = "current_month" } = {}) {
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const [
    totalCountRes,
    inStockCountRes,
    outOfStockCountRes,
    lowStockCountRes,
    lowStockItemsRes,
    todayTxsRes,
  ] = await Promise.all([
    supabase.from("items").select("*", { count: "exact", head: true }),
    supabase.from("items").select("*", { count: "exact", head: true }).gt("stock", 10),
    supabase.from("items").select("*", { count: "exact", head: true }).eq("stock", 0),
    supabase.from("items").select("*", { count: "exact", head: true }).gt("stock", 0).lte("stock", 10),
    supabase.from("items").select("*").lte("stock", 10).order("stock").limit(500),
    supabase.from("transactions").select("transaction_id, total_amount").gte("created_at", today.toISOString()),
  ]);

  const todayTransactions = todayTxsRes.data || [];
  const todayRevenue = todayTransactions.reduce(
    (sum, t) => sum + t.total_amount,
    0,
  );
  const todayTxIds = new Set(todayTransactions.map((t) => t.transaction_id));
  const [todayProfit, profit] = await Promise.all([
    calculateProfitForTransactionIds(todayTxIds),
    getProfitForPeriod({ profitPeriod, now }),
  ]);

  return {
    stock: {
      total_items: totalCountRes.count || 0,
      in_stock: inStockCountRes.count || 0,
      out_of_stock: outOfStockCountRes.count || 0,
      low_stock: lowStockCountRes.count || 0,
    },
    lowStockItems: lowStockItemsRes.data || [],
    today: {
      transaction_count: todayTransactions.length,
      total_revenue: todayRevenue,
      avg_transaction:
        todayTransactions.length > 0
          ? todayRevenue / todayTransactions.length
          : 0,
      total_profit: todayProfit,
    },
    profit,
  };
}

// Reset transactions (keep inventory)
export async function resetTransactions() {
  await supabase.from("transaction_items").delete().neq("id", 0);
  await supabase.from("transactions").delete().neq("id", 0);
  return {
    success: true,
    message: "All transactions cleared. Inventory preserved.",
  };
}

// Export all data as a downloadable JSON file
export async function exportDataAsFile() {
  const [
    { data: users },
    { data: items },
    { data: categories },
    { data: transactions },
    { data: transaction_items },
  ] = await Promise.all([
    supabase.from("users").select("*"),
    supabase.from("items").select("*"),
    supabase.from("categories").select("*"),
    supabase.from("transactions").select("*"),
    supabase.from("transaction_items").select("*"),
  ]);

  const payload = {
    users,
    items,
    categories,
    transactions,
    transaction_items,
    exported_at: new Date().toISOString(),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `grocery_store_backup_${new Date().toISOString().split("T")[0]}.json`;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
  return { success: true, message: "Data exported successfully" };
}

// Import data from a JSON backup file (upsert into Supabase)
export async function importDataFromObject(data) {
  if (data.categories?.length)
    await supabase
      .from("categories")
      .upsert(data.categories, { onConflict: "id" });
  if (data.users?.length)
    await supabase.from("users").upsert(data.users, { onConflict: "id" });
  if (data.items?.length)
    await supabase.from("items").upsert(data.items, { onConflict: "id" });
  if (data.transactions?.length)
    await supabase
      .from("transactions")
      .upsert(data.transactions, { onConflict: "id" });
  if (data.transaction_items?.length)
    await supabase
      .from("transaction_items")
      .upsert(data.transaction_items, { onConflict: "id" });
  return { success: true, message: "Data imported successfully" };
}
